import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect, useCallback } from 'react';
import { API } from '../../utils/config';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowRightLeft, 
  Calendar, 
  Clock, 
  User, 
  Home, 
  ChevronRight, 
  RefreshCw,
  CalendarClock,
  FileCheck,
  PenLine,
  CheckCircle2,
  XCircle,
  AlertCircle
} from 'lucide-react';


const PassaggiConsegne = () => {
  const [handovers, setHandovers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const token = localStorage.getItem('hw_token');

  const fetchHandovers = useCallback(async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API}/api/handovers?status=active`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setHandovers(data.handovers || []);
      }
    } catch (error) {
      console.error('Error fetching handovers:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchHandovers();
  }, [fetchHandovers]);

  const getStatusConfig = (status) => {
    const configs = {
      pending_schedule: {
        label: 'Da fissare',
        color: 'bg-yellow-100 text-yellow-800 border-yellow-200',
        icon: CalendarClock,
        description: 'In attesa di fissare appuntamento'
      },
      scheduled: {
        label: 'Programmato',
        color: 'bg-blue-100 text-blue-800 border-blue-200',
        icon: Calendar,
        description: 'Appuntamento fissato'
      },
      in_progress: {
        label: 'In corso',
        color: 'bg-purple-100 text-purple-800 border-purple-200',
        icon: PenLine,
        description: 'Compilazione verbale in corso'
      },
      pending_signatures: {
        label: 'Firme',
        color: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: FileCheck,
        description: 'In attesa delle firme'
      },
      completed: {
        label: 'Completato',
        color: 'bg-green-100 text-green-800 border-green-200',
        icon: CheckCircle2,
        description: 'Passaggio completato'
      },
      cancelled: {
        label: 'Annullato',
        color: 'bg-red-100 text-red-800 border-red-200',
        icon: XCircle,
        description: 'Passaggio annullato'
      }
    };
    return configs[status] || configs.pending_schedule;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleDateString('it-IT', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Passaggi Consegne</h1>
          <p className="text-sm text-gray-500 mt-1">
            Gestisci i passaggi di consegna degli immobili
          </p>
        </div>
        <Button onClick={fetchHandovers} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Aggiorna
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Da fissare', value: handovers.filter(h => h.status === 'pending_schedule').length, color: 'text-yellow-600' },
          { label: 'Programmati', value: handovers.filter(h => h.status === 'scheduled').length, color: 'text-blue-600' },
          { label: 'In corso', value: handovers.filter(h => ['in_progress', 'pending_signatures'].includes(h.status)).length, color: 'text-purple-600' },
          { label: 'Completati', value: handovers.filter(h => h.status === 'completed').length, color: 'text-green-600' }
        ].map((stat, i) => (
          <Card key={i} className="bg-white">
            <CardContent className="p-4">
              <div className="text-sm text-gray-500">{stat.label}</div>
              <div className={`text-2xl font-bold ${stat.color}`}>{stat.value}</div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Handovers List */}
      {handovers.length === 0 ? (
        <Card className="bg-white">
          <CardContent className="p-12 text-center">
            <ArrowRightLeft className="w-16 h-16 mx-auto text-gray-300 mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Nessun passaggio consegne
            </h3>
            <p className="text-gray-500">
              Non ci sono passaggi consegne attivi per i tuoi immobili
            </p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {handovers.map((handover) => {
            const statusConfig = getStatusConfig(handover.status);
            const StatusIcon = statusConfig.icon;
            
            return (
              <Card 
                key={handover.id} 
                className="bg-white hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => navigate(`/responsabile/passaggio-consegne/${handover.id}`)}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      {/* Property Info */}
                      <div className="flex items-center gap-3 mb-4">
                        <div className="p-2 bg-slate-100 rounded-lg">
                          <Home className="w-5 h-5 text-slate-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-gray-900">
                            {handover.property_name}
                          </h3>
                          {handover.property_address && (
                            <p className="text-sm text-gray-500">
                              {handover.property_address}
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Responsabili */}
                      <div className="grid grid-cols-2 gap-4 mb-4">
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-red-500" />
                          <div>
                            <p className="text-xs text-gray-500">Uscente</p>
                            <p className="text-sm font-medium">{handover.outgoing_responsible?.name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <User className="w-4 h-4 text-green-500" />
                          <div>
                            <p className="text-xs text-gray-500">Entrante</p>
                            <p className="text-sm font-medium">{handover.incoming_responsible?.name}</p>
                          </div>
                        </div>
                      </div>

                      {/* Appointment Info */}
                      {handover.confirmed_date && (
                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar className="w-4 h-4" />
                          <span>Appuntamento: {formatDate(handover.confirmed_date)}</span>
                          {handover.confirmed_time && (
                            <>
                              <Clock className="w-4 h-4 ml-2" />
                              <span>{handover.confirmed_time}</span>
                            </>
                          )}
                        </div>
                      )}

                      {/* Deadline Warning */}
                      {handover.deadline && new Date(handover.deadline) < new Date() && handover.status !== 'completed' && (
                        <div className="flex items-center gap-2 mt-2 text-sm text-red-600">
                          <AlertCircle className="w-4 h-4" />
                          <span>Scaduto il {formatDate(handover.deadline)}</span>
                        </div>
                      )}
                    </div>

                    {/* Status Badge & Arrow */}
                    <div className="flex items-center gap-4">
                      <Badge className={`${statusConfig.color} flex items-center gap-1 px-3 py-1`}>
                        <StatusIcon className="w-3 h-3" />
                        {statusConfig.label}
                      </Badge>
                      <ChevronRight className="w-5 h-5 text-gray-400" />
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PassaggiConsegne;
